import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  LayoutDashboard,
  FileText,
  MessageSquare,
  FileSignature,
  LogOut,
  Upload,
  CheckCircle2,
  Clock,
  AlertCircle,
  Send,
  Download,
  Loader2,
  User,
  Building2,
  ChevronRight,
  CalendarDays,
  PoundSterling,
  ArrowRight,
  CircleDot,
  Check,
  X as XIcon,
  Shield,
  Briefcase,
  FileUp,
  Eye,
  Settings,
  Bell,
  BellOff,
  FileWarning,
  TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ PROGRESS STEPS CONFIG ============
const JOURNEY_STEPS = [
  { key: 'applied', label: 'Applied', statuses: ['new', 'contacted'] },
  { key: 'documents', label: 'Documents', statuses: ['qualified', 'docs_requested', 'docs_received'] },
  { key: 'review', label: 'Under Review', statuses: ['under_review'] },
  { key: 'offer', label: 'Offer', statuses: ['offer_sent'] },
  { key: 'funded', label: 'Funded', statuses: ['approved', 'funded'] },
];

function getStepIndex(status) {
  for (let i = 0; i < JOURNEY_STEPS.length; i++) {
    if (JOURNEY_STEPS[i].statuses.includes(status)) return i;
  }
  return 0;
}

function getTimeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}


// Status config
const statusConfig = {
  required: { color: 'bg-red-50 text-red-700 border-red-200', label: 'Required', icon: AlertCircle },
  uploaded: { color: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Uploaded', icon: Clock },
  under_review: { color: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Under Review', icon: Eye },
  approved: { color: 'bg-green-50 text-green-700 border-green-200', label: 'Approved', icon: CheckCircle2 },
  rejected: { color: 'bg-red-50 text-red-700 border-red-200', label: 'Rejected', icon: XIcon }
};

const docIcons = {
  'id-card': User,
  'home': Building2,
  'building-columns': Building2,
  'credit-card': FileText,
  'chart-line': FileText,
  'certificate': FileSignature,
  'receipt': FileText,
  'file-invoice': FileText,
  'lightbulb': FileText,
  'gem': FileText,
  'signature': FileSignature,
  'briefcase': Briefcase
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP', minimumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
  });
};

// ============ PROGRESS STEPPER COMPONENT ============
function ProgressStepper({ currentStatus }) {
  const currentIdx = getStepIndex(currentStatus);
  const isDeclined = currentStatus === 'declined';

  if (isDeclined) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6" data-testid="progress-stepper-declined">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <XIcon className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="font-semibold text-red-800">Application Declined</p>
            <p className="text-sm text-red-600">Please contact your advisor for more information.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="progress-stepper">
      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-6">Your Funding Journey</h3>
      <div className="flex items-center justify-between relative">
        {/* Connecting line */}
        <div className="absolute top-5 left-[10%] right-[10%] h-0.5 bg-slate-200 z-0" />
        <div
          className="absolute top-5 left-[10%] h-0.5 bg-blue-700 z-0 transition-all duration-700"
          style={{ width: `${Math.min(currentIdx / (JOURNEY_STEPS.length - 1), 1) * 80}%` }}
        />

        {JOURNEY_STEPS.map((step, idx) => {
          const isCompleted = idx < currentIdx;
          const isCurrent = idx === currentIdx;
          const isFuture = idx > currentIdx;

          return (
            <div key={step.key} className="flex flex-col items-center z-10 relative" style={{ width: '20%' }}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                  isCompleted
                    ? 'bg-blue-700 border-blue-700 text-white'
                    : isCurrent
                    ? 'bg-white border-blue-700 text-blue-700 ring-4 ring-blue-100'
                    : 'bg-white border-slate-300 text-slate-400'
                }`}
              >
                {isCompleted ? (
                  <Check className="w-5 h-5" />
                ) : isCurrent ? (
                  <CircleDot className="w-5 h-5" />
                ) : (
                  <span className="text-sm font-medium">{idx + 1}</span>
                )}
              </div>
              <span
                className={`text-xs mt-2 font-medium text-center ${
                  isCompleted ? 'text-blue-700' : isCurrent ? 'text-blue-700 font-semibold' : 'text-slate-400'
                }`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ DASHBOARD OVERVIEW ============
function PortalOverview({ dashboard, onRefresh }) {
  const navigate = useNavigate();

  if (!dashboard?.has_application) {
    return (
      <div className="text-center py-20" data-testid="no-application">
        <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <FileText className="w-10 h-10 text-slate-400" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-3 font-['Outfit']">No Application Found</h2>
        <p className="text-slate-500 mb-8 max-w-sm mx-auto">Start your funding journey by submitting a loan application.</p>
        <Link to="/#apply">
          <Button className="bg-blue-700 hover:bg-blue-800 h-11 px-8" data-testid="apply-now-portal">
            Apply Now <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    );
  }

  const { application, documents, messages, agreements, advisor, activities } = dashboard;
  const docsProgress = documents.total > 0 ? ((documents.approved + documents.uploaded) / documents.total) * 100 : 0;

  return (
    <div data-testid="portal-overview" className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 rounded-2xl p-6 md:p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full border border-white/30" />
          <div className="absolute -bottom-8 -left-8 w-48 h-48 rounded-full border border-white/20" />
        </div>
        <div className="relative z-10">
          <p className="text-blue-300 text-sm font-medium mb-1">Welcome back</p>
          <h1 className="text-2xl md:text-3xl font-bold font-['Outfit'] mb-1">{application.full_name || application.business_name}</h1>
          <p className="text-slate-400 text-sm">{application.business_name} &middot; Applied {formatDate(application.created_at)}</p>
        </div>
      </div>

      {/* Progress Stepper */}
      <ProgressStepper currentStatus={application.status} />

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <PoundSterling className="w-4 h-4 text-blue-700" />
            <span className="text-xs font-medium text-slate-500 uppercase">Loan Amount</span>
          </div>
          <p className="text-xl font-bold text-slate-900 font-['Outfit']">{formatCurrency(application.loan_amount)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-blue-700" />
            <span className="text-xs font-medium text-slate-500 uppercase">Documents</span>
          </div>
          <p className="text-xl font-bold text-slate-900 font-['Outfit']">{documents.approved + documents.uploaded} / {documents.total}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-4 h-4 text-blue-700" />
            <span className="text-xs font-medium text-slate-500 uppercase">Messages</span>
          </div>
          <p className="text-xl font-bold text-slate-900 font-['Outfit']">{messages.unread} unread</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileSignature className="w-4 h-4 text-blue-700" />
            <span className="text-xs font-medium text-slate-500 uppercase">Agreements</span>
          </div>
          <p className="text-xl font-bold text-slate-900 font-['Outfit']">{agreements.pending} pending</p>
        </div>
      </div>

      {/* Documents Progress + Action Required */}
      {documents.required > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-4">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-amber-800 mb-1">Action Required: {documents.required} Document(s) Needed</p>
            <p className="text-sm text-amber-700 mb-3">Upload your required documents to progress your application.</p>
            <Button
              size="sm"
              onClick={() => navigate('/portal/documents')}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="upload-docs-cta"
            >
              <Upload className="w-4 h-4 mr-2" /> Upload Documents
            </Button>
          </div>
        </div>
      )}

      {/* Documents Progress Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm font-medium text-slate-700">Document Completion</span>
          <span className="text-sm font-bold text-blue-700">{Math.round(docsProgress)}%</span>
        </div>
        <Progress value={docsProgress} className="h-2" />
        <div className="flex justify-between mt-2 text-xs text-slate-400">
          <span>{documents.approved} approved</span>
          <span>{documents.uploaded} uploaded</span>
          <span>{documents.required} required</span>
        </div>
      </div>

      {/* Two Column: Quick Actions + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-2 space-y-3">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Quick Actions</h3>
          {[
            { label: 'Documents', desc: `${documents.required} required`, icon: FileUp, path: '/portal/documents', color: 'bg-blue-50 text-blue-700' },
            { label: 'Messages', desc: `${messages.unread} unread`, icon: MessageSquare, path: '/portal/messages', color: 'bg-indigo-50 text-indigo-700' },
            { label: 'Agreements', desc: `${agreements.pending} pending`, icon: FileSignature, path: '/portal/agreements', color: 'bg-purple-50 text-purple-700' },
          ].map((item) => (
            <button
              key={item.label}
              onClick={() => navigate(item.path)}
              className="w-full bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4 hover:border-blue-300 hover:shadow-sm transition-all group text-left"
              data-testid={`quick-${item.label.toLowerCase()}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.color}`}>
                <item.icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-900 text-sm">{item.label}</p>
                <p className="text-xs text-slate-500">{item.desc}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-700 transition-colors" />
            </button>
          ))}
        </div>

        {/* Activity Timeline */}
        <div className="lg:col-span-3">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Recent Activity</h3>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            {(!activities || activities.length === 0) ? (
              <p className="text-sm text-slate-400 text-center py-6">No activity yet.</p>
            ) : (
              <div className="space-y-4">
                {activities.slice(0, 5).map((activity, idx) => (
                  <div key={idx} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        activity.type === 'application_submitted' ? 'bg-blue-100 text-blue-700' :
                        activity.type === 'document_uploaded' ? 'bg-green-100 text-green-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {activity.type === 'application_submitted' ? <Briefcase className="w-4 h-4" /> :
                         activity.type === 'document_uploaded' ? <CheckCircle2 className="w-4 h-4" /> :
                         <CalendarDays className="w-4 h-4" />}
                      </div>
                      {idx < Math.min(activities.length, 5) - 1 && (
                        <div className="w-px h-full bg-slate-200 mt-1" />
                      )}
                    </div>
                    <div className="pb-4">
                      <p className="text-sm font-medium text-slate-900">{activity.title}</p>
                      <p className="text-xs text-slate-500">{activity.description}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{formatDateTime(activity.date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Advisor Card */}
      {advisor && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Your Dedicated Advisor</h3>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-lg font-bold text-blue-700">{advisor.name?.charAt(0) || 'A'}</span>
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-900">{advisor.name}</p>
              <p className="text-sm text-slate-500">{advisor.email}</p>
            </div>
            <Button
              onClick={() => navigate('/portal/messages')}
              className="bg-blue-700 hover:bg-blue-800"
              data-testid="message-advisor-btn"
            >
              <Send className="w-4 h-4 mr-2" /> Message
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ DOCUMENTS PAGE ============
function PortalDocuments() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client/documents`, { withCredentials: true });
      setDocuments(response.data.documents || []);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (docType, file) => {
    setUploading(docType);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API_URL}/api/client/documents/${docType}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      fetchDocuments();
    } catch (error) {
      const message = error.response?.data?.detail || 'Upload failed';
      toast.error(typeof message === 'string' ? message : 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const requiredDocs = documents.filter(d => d.status === 'required');
  const submittedDocs = documents.filter(d => d.status !== 'required');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  return (
    <div data-testid="portal-documents" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Documents</h1>
        <p className="text-slate-500 mt-1">Upload the required documents to progress your application.</p>
      </div>

      {/* Progress summary */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-slate-700">Upload Progress</span>
          <span className="text-sm font-bold text-blue-700">{submittedDocs.length} / {documents.length}</span>
        </div>
        <Progress value={documents.length > 0 ? (submittedDocs.length / documents.length) * 100 : 0} className="h-2" />
      </div>

      {requiredDocs.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Action Required</p>
            <p className="text-sm text-amber-700">Please upload the following {requiredDocs.length} document(s) to continue.</p>
          </div>
        </div>
      )}

      {/* Required Documents */}
      {requiredDocs.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Required Documents</h2>
          <div className="space-y-3">
            {requiredDocs.map((doc) => {
              const IconComponent = docIcons[doc.icon] || FileText;
              return (
                <div key={doc.type} className="bg-white border-2 border-dashed border-red-200 rounded-xl p-5 hover:border-blue-300 transition-colors" data-testid={`doc-${doc.type}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-red-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{doc.name}</h3>
                        <p className="text-sm text-slate-500 mt-0.5">{doc.description}</p>
                        <Badge className="mt-2 bg-red-50 text-red-700 border-red-200">
                          <AlertCircle className="w-3 h-3 mr-1" /> Required
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <input
                        type="file"
                        id={`upload-${doc.type}`}
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        onChange={(e) => e.target.files?.[0] && handleUpload(doc.type, e.target.files[0])}
                      />
                      <label htmlFor={`upload-${doc.type}`}>
                        <Button asChild className="bg-blue-700 hover:bg-blue-800 cursor-pointer" disabled={uploading === doc.type}>
                          <span>
                            {uploading === doc.type ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <><Upload className="w-4 h-4 mr-2" /> Upload</>
                            )}
                          </span>
                        </Button>
                      </label>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Submitted Documents */}
      {submittedDocs.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Submitted Documents</h2>
          <div className="space-y-3">
            {submittedDocs.map((doc) => {
              const StatusIcon = statusConfig[doc.status]?.icon || FileText;
              const IconComponent = docIcons[doc.icon] || FileText;
              return (
                <div key={doc.type} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`doc-${doc.type}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{doc.name}</h3>
                        <p className="text-sm text-slate-500 mt-0.5">{doc.description}</p>
                        <Badge className={`mt-2 ${statusConfig[doc.status]?.color || 'bg-slate-100'}`}>
                          <StatusIcon className="w-3 h-3 mr-1" /> {statusConfig[doc.status]?.label || doc.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="hidden sm:inline">Uploaded</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upload Guidelines */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-blue-700" /> Upload Guidelines
        </h3>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-700" />Accepted formats: PDF, JPG, PNG, DOC, DOCX</li>
          <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-700" />Maximum file size: 10MB per document</li>
          <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-700" />Ensure documents are clear and readable</li>
          <li className="flex items-center gap-2"><Shield className="w-4 h-4 text-blue-700" />All documents are encrypted and stored securely</li>
        </ul>
      </div>
    </div>
  );
}

// ============ MESSAGES PAGE ============
function PortalMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchMessages(); }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client/messages`, { withCredentials: true });
      setMessages(response.data.messages || []);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/client/messages`, { content: newMessage }, { withCredentials: true });
      setNewMessage('');
      toast.success('Message sent');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="portal-messages" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Messages</h1>
        <p className="text-slate-500 mt-1">Communicate with your dedicated advisor.</p>
      </div>

      {/* Message Input */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <Textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type your message here..."
          rows={3}
          className="mb-3 resize-none"
          data-testid="message-input"
        />
        <div className="flex justify-end">
          <Button
            onClick={handleSend}
            disabled={sending || !newMessage.trim()}
            className="bg-blue-700 hover:bg-blue-800"
            data-testid="send-message-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Send Message
          </Button>
        </div>
      </div>

      {/* Messages List */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin text-blue-700" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-16 bg-white border border-slate-200 rounded-xl">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No messages yet. Start a conversation with your advisor.</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`p-4 rounded-xl ${
                msg.sender_role === 'client'
                  ? 'bg-blue-50 border border-blue-200 ml-8'
                  : 'bg-white border border-slate-200 mr-8'
              }`}
              data-testid={`message-${msg.id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-slate-900 text-sm">{msg.sender_name}</span>
                <span className="text-xs text-slate-500">{formatDateTime(msg.created_at)}</span>
              </div>
              <p className="text-slate-700 text-sm">{msg.content}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ============ AGREEMENTS PAGE ============
function PortalAgreements() {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signDialog, setSignDialog] = useState({ open: false, agreement: null });
  const [signatureName, setSignatureName] = useState('');
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [signing, setSigning] = useState(false);

  useEffect(() => { fetchAgreements(); }, []);

  const fetchAgreements = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client/agreements`, { withCredentials: true });
      setAgreements(response.data.agreements || []);
    } catch (error) {
      toast.error('Failed to load agreements');
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!signatureName.trim() || !signDialog.agreement || !agreeChecked) return;
    setSigning(true);
    try {
      await axios.post(`${API_URL}/api/client/agreements/${signDialog.agreement.id}/sign`, {
        signature_name: signatureName,
        signature_date: new Date().toISOString().split('T')[0]
      }, { withCredentials: true });
      toast.success('Agreement signed successfully!');
      setSignDialog({ open: false, agreement: null });
      setSignatureName('');
      setAgreeChecked(false);
      fetchAgreements();
    } catch (error) {
      toast.error('Failed to sign agreement');
    } finally {
      setSigning(false);
    }
  };

  const handleDownload = async (agreementId) => {
    try {
      const response = await axios.get(`${API_URL}/api/agreements/${agreementId}/pdf`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `QuickFinance_Agreement_${agreementId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download agreement');
    }
  };

  return (
    <div data-testid="portal-agreements" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Agreements</h1>
        <p className="text-slate-500 mt-1">Review and sign your loan agreements.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-blue-700" />
        </div>
      ) : agreements.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-xl">
          <FileSignature className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 mb-1">No agreements available yet.</p>
          <p className="text-sm text-slate-400">Your advisor will send one when your application is ready.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {agreements.map((agreement) => (
            <div key={agreement.id} className="bg-white border border-slate-200 rounded-xl p-6" data-testid={`agreement-${agreement.id}`}>
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="font-semibold text-slate-900 font-['Outfit']">Loan Agreement</h3>
                  <p className="text-sm text-slate-500">Created {formatDate(agreement.created_at)}</p>
                </div>
                <Badge className={agreement.signed ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                  {agreement.signed ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Signed</> : <><Clock className="w-3 h-3 mr-1" /> Pending Signature</>}
                </Badge>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Loan Amount</p>
                  <p className="font-bold text-slate-900">{formatCurrency(agreement.loan_amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Interest Rate</p>
                  <p className="font-bold text-slate-900">{agreement.interest_rate}% APR</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Term</p>
                  <p className="font-bold text-slate-900">{agreement.term_months} months</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Monthly Payment</p>
                  <p className="font-bold text-blue-700">{formatCurrency(agreement.monthly_payment)}</p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => handleDownload(agreement.id)} data-testid={`download-${agreement.id}`}>
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                {!agreement.signed && (
                  <Button
                    onClick={() => { setSignDialog({ open: true, agreement }); setAgreeChecked(false); setSignatureName(''); }}
                    className="bg-blue-700 hover:bg-blue-800"
                    data-testid={`sign-${agreement.id}`}
                  >
                    <FileSignature className="w-4 h-4 mr-2" /> Sign Agreement
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sign Dialog */}
      <Dialog open={signDialog.open} onOpenChange={(open) => !open && setSignDialog({ open: false, agreement: null })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Sign Agreement</DialogTitle>
            <DialogDescription>
              By signing below, you confirm you have read and agree to all terms of this loan agreement.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {signDialog.agreement && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-1">
                <div className="flex justify-between"><span className="text-sm text-slate-500">Loan Amount</span><span className="text-sm font-bold">{formatCurrency(signDialog.agreement.loan_amount)}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Monthly Payment</span><span className="text-sm font-bold">{formatCurrency(signDialog.agreement.monthly_payment)}</span></div>
                <div className="flex justify-between"><span className="text-sm text-slate-500">Total Repayable</span><span className="text-sm font-bold">{formatCurrency(signDialog.agreement.total_repayable)}</span></div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-slate-700">Type your full legal name as signature</label>
              <Input
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                placeholder="Enter your full legal name"
                className="mt-1.5"
                data-testid="signature-input"
              />
              {signatureName.trim() && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-lg italic text-blue-900 font-serif">{signatureName}</p>
                  <p className="text-xs text-blue-500 mt-1">Digital Signature Preview</p>
                </div>
              )}
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="agree-terms"
                className="mt-1 accent-blue-700"
                checked={agreeChecked}
                onChange={(e) => setAgreeChecked(e.target.checked)}
              />
              <label htmlFor="agree-terms" className="text-sm text-slate-600">
                I confirm that I am authorized to sign this agreement, have read all terms and conditions, and accept this as a legally binding agreement.
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSignDialog({ open: false, agreement: null })}>Cancel</Button>
            <Button
              onClick={handleSign}
              disabled={signing || !signatureName.trim() || !agreeChecked}
              className="bg-blue-700 hover:bg-blue-800"
              data-testid="confirm-sign-btn"
            >
              {signing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sign & Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ SETTINGS PAGE ============
function PortalSettings({ user }) {
  const [profile, setProfile] = useState({ name: user?.name || '', phone: '' });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [notifPrefs, setNotifPrefs] = useState({ email_notifications: true, sms_notifications: true });
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const [profileRes, prefsRes] = await Promise.all([
          axios.get(`${API_URL}/api/client/profile`, { withCredentials: true }),
          axios.get(`${API_URL}/api/client/notification-preferences`, { withCredentials: true })
        ]);
        setProfile({ name: profileRes.data.name || '', phone: profileRes.data.phone || '' });
        setNotifPrefs({
          email_notifications: prefsRes.data.email_notifications ?? true,
          sms_notifications: prefsRes.data.sms_notifications ?? true
        });
      } catch (e) { /* ignore */ }
      finally { setLoadingProfile(false); }
    };
    fetchProfile();
  }, []);

  const handleProfileSave = async () => {
    setSavingProfile(true);
    try {
      await axios.patch(`${API_URL}/api/client/profile`, profile, { withCredentials: true });
      toast.success('Profile updated');
    } catch (e) { toast.error('Failed to update profile'); }
    finally { setSavingProfile(false); }
  };

  const handlePasswordChange = async () => {
    if (passwords.new_password !== passwords.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwords.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSavingPassword(true);
    try {
      await axios.post(`${API_URL}/api/client/change-password`, {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setPasswords({ current_password: '', new_password: '', confirm: '' });
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to change password';
      toast.error(typeof msg === 'string' ? msg : 'Failed');
    }
    finally { setSavingPassword(false); }
  };

  const handleNotifToggle = async (key, value) => {
    setSavingPrefs(true);
    const updated = { ...notifPrefs, [key]: value };
    setNotifPrefs(updated);
    try {
      await axios.patch(`${API_URL}/api/client/notification-preferences`, { [key]: value }, { withCredentials: true });
      toast.success(`${key === 'email_notifications' ? 'Email' : 'SMS'} notifications ${value ? 'enabled' : 'disabled'}`);
    } catch (e) {
      setNotifPrefs(notifPrefs); // revert
      toast.error('Failed to update preferences');
    }
    finally { setSavingPrefs(false); }
  };

  if (loadingProfile) {
    return <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-blue-700" /></div>;
  }

  return (
    <div data-testid="portal-settings" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account and preferences.</p>
      </div>

      {/* Profile */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Profile Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Full Name</label>
            <Input value={profile.name} onChange={(e) => setProfile(p => ({...p, name: e.target.value}))} className="mt-1" data-testid="settings-name" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Phone Number</label>
            <Input value={profile.phone} onChange={(e) => setProfile(p => ({...p, phone: e.target.value}))} className="mt-1" data-testid="settings-phone" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={handleProfileSave} disabled={savingProfile} className="bg-blue-700 hover:bg-blue-800" data-testid="save-profile-btn">
            {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save Changes
          </Button>
        </div>
      </div>

      {/* Password */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Change Password</h3>
        <div className="space-y-4 max-w-md">
          <div>
            <label className="text-sm font-medium text-slate-700">Current Password</label>
            <Input type="password" value={passwords.current_password} onChange={(e) => setPasswords(p => ({...p, current_password: e.target.value}))} className="mt-1" data-testid="current-password" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">New Password</label>
            <Input type="password" value={passwords.new_password} onChange={(e) => setPasswords(p => ({...p, new_password: e.target.value}))} className="mt-1" data-testid="new-password" placeholder="Minimum 8 characters" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Confirm New Password</label>
            <Input type="password" value={passwords.confirm} onChange={(e) => setPasswords(p => ({...p, confirm: e.target.value}))} className="mt-1" data-testid="confirm-password" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={handlePasswordChange} disabled={savingPassword || !passwords.current_password || !passwords.new_password} className="bg-blue-700 hover:bg-blue-800" data-testid="change-password-btn">
            {savingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Change Password
          </Button>
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Notification Preferences</h3>
        <p className="text-sm text-slate-500 mb-4">Choose how you'd like to receive updates about your application.</p>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              {notifPrefs.email_notifications ? <Bell className="w-5 h-5 text-blue-700" /> : <BellOff className="w-5 h-5 text-slate-400" />}
              <div>
                <p className="font-medium text-slate-900 text-sm">Email Notifications</p>
                <p className="text-xs text-slate-500">Status updates, document reviews, messages from advisors</p>
              </div>
            </div>
            <button
              onClick={() => handleNotifToggle('email_notifications', !notifPrefs.email_notifications)}
              disabled={savingPrefs}
              className={`relative w-11 h-6 rounded-full transition-colors ${notifPrefs.email_notifications ? 'bg-blue-700' : 'bg-slate-300'}`}
              data-testid="toggle-email-notifications"
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${notifPrefs.email_notifications ? 'translate-x-5' : ''}`} />
            </button>
          </div>
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              {notifPrefs.sms_notifications ? <Bell className="w-5 h-5 text-blue-700" /> : <BellOff className="w-5 h-5 text-slate-400" />}
              <div>
                <p className="font-medium text-slate-900 text-sm">SMS Notifications</p>
                <p className="text-xs text-slate-500">Text alerts for key milestones and urgent updates</p>
              </div>
            </div>
            <button
              onClick={() => handleNotifToggle('sms_notifications', !notifPrefs.sms_notifications)}
              disabled={savingPrefs}
              className={`relative w-11 h-6 rounded-full transition-colors ${notifPrefs.sms_notifications ? 'bg-blue-700' : 'bg-slate-300'}`}
              data-testid="toggle-sms-notifications"
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${notifPrefs.sms_notifications ? 'translate-x-5' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Account Info */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
        <h3 className="font-semibold text-slate-900 mb-2">Account</h3>
        <p className="text-sm text-slate-600"><strong>Email:</strong> {user?.email}</p>
        <p className="text-xs text-slate-400 mt-2">To change your email address, please contact your advisor.</p>
      </div>
    </div>
  );
}

// ============ MAIN PORTAL LAYOUT ============
export default function ClientPortalPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = React.useRef(null);
  const prevUnreadRef = React.useRef(0);

  useEffect(() => { checkAuth(); }, []);

  // Poll notifications every 30s
  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Close bell dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/client/notifications`, { withCredentials: true });
      setNotifications(res.data.notifications);
      const newUnread = res.data.unread_count;
      if (newUnread > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        const latest = res.data.notifications.find(n => !n.read);
        if (latest) toast.info(latest.title, { description: latest.message.substring(0, 80) });
      }
      prevUnreadRef.current = newUnread;
      setUnreadCount(newUnread);
    } catch {}
  };

  const markRead = async (id) => {
    await axios.patch(`${API_URL}/api/client/notifications/${id}/read`, {}, { withCredentials: true });
    fetchNotifications();
  };

  const markAllRead = async () => {
    await axios.patch(`${API_URL}/api/client/notifications/read-all`, {}, { withCredentials: true });
    fetchNotifications();
  };

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      if (response.data.role !== 'client') throw new Error('Not a client');
      setUser(response.data);
      fetchDashboard();
    } catch (error) {
      navigate('/portal/login');
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client/dashboard`, { withCredentials: true });
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_user');
    navigate('/portal/login');
  };

  const navItems = [
    { path: '/portal/dashboard', label: 'Overview', icon: LayoutDashboard },
    { path: '/portal/documents', label: 'Documents', icon: FileText, badge: dashboard?.documents?.required },
    { path: '/portal/messages', label: 'Messages', icon: MessageSquare, badge: dashboard?.messages?.unread },
    { path: '/portal/agreements', label: 'Agreements', icon: FileSignature, badge: dashboard?.agreements?.pending },
    { path: '/portal/settings', label: 'Settings', icon: Settings }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-portal">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 transform transition-transform duration-200 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-2 px-6 py-5 border-b border-slate-800">
            <div className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl font-['Outfit']">Q</span>
            </div>
            <span className="text-xl font-bold text-white font-['Outfit']">QuickFinance</span>
          </div>

          <nav className="flex-1 px-4 py-6 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || (item.path === '/portal/dashboard' && location.pathname === '/portal');
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'bg-blue-700 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </div>
                  {item.badge > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{item.badge}</span>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="px-4 py-4 border-t border-slate-800">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 bg-blue-800 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">{user?.name?.charAt(0) || 'C'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'Client'}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800 mt-2"
              onClick={handleLogout}
              data-testid="portal-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-3" /> Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <div className="lg:ml-64">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-[55]">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-14">
            <button
              className="lg:hidden p-2 text-slate-600"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex items-center gap-3">
              {/* Notification Bell */}
              <div ref={bellRef} className="relative" data-testid="notification-bell-wrapper">
                <button
                  onClick={() => setBellOpen(!bellOpen)}
                  className="relative p-2 text-slate-500 hover:text-slate-800 transition-colors"
                  data-testid="notification-bell-btn"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="notification-badge">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {bellOpen && (
                  <div className="absolute left-0 top-12 w-80 sm:w-96 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden" data-testid="notification-dropdown">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/80">
                      <span className="text-sm font-semibold text-slate-800 font-['Outfit']">Notifications</span>
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid="mark-all-read-btn">
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                      {notifications.length === 0 ? (
                        <div className="py-10 text-center">
                          <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-sm text-slate-400">No notifications yet</p>
                        </div>
                      ) : (
                        notifications.slice(0, 15).map((n) => {
                          const Icon = n.type === 'status_change' ? TrendingUp : n.type === 'new_message' ? MessageSquare : n.type === 'docs_request' ? FileWarning : Bell;
                          const iconColor = n.type === 'status_change' ? 'bg-blue-100 text-blue-600' : n.type === 'new_message' ? 'bg-violet-100 text-violet-600' : n.type === 'docs_request' ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-500';
                          const age = getTimeAgo(n.created_at);
                          return (
                            <button
                              key={n.id}
                              onClick={() => { markRead(n.id); if (n.link) navigate(n.link); setBellOpen(false); }}
                              className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-slate-50 transition-colors ${!n.read ? 'bg-blue-50/40' : ''}`}
                              data-testid={`notification-item-${n.id}`}
                            >
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${iconColor}`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className={`text-xs font-semibold truncate ${!n.read ? 'text-slate-900' : 'text-slate-600'}`}>{n.title}</p>
                                  {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0"></span>}
                                </div>
                                <p className="text-[11px] text-slate-500 truncate mt-0.5">{n.message}</p>
                                <p className="text-[10px] text-slate-400 mt-0.5">{age}</p>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
              <Link to="/" className="text-sm text-slate-500 hover:text-blue-700 transition-colors">
                Visit Website
              </Link>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-5xl">
          <Routes>
            <Route path="/" element={<PortalOverview dashboard={dashboard} onRefresh={fetchDashboard} />} />
            <Route path="/dashboard" element={<PortalOverview dashboard={dashboard} onRefresh={fetchDashboard} />} />
            <Route path="/documents" element={<PortalDocuments />} />
            <Route path="/messages" element={<PortalMessages />} />
            <Route path="/agreements" element={<PortalAgreements />} />
            <Route path="/settings" element={<PortalSettings user={user} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
